import React, { Component } from 'react';
import styled from 'styled-components';
import logoSrc from 'assets/images/logo_blue.png';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-family: "Gotham A", "Gotham B", "Helvetica Neue", Helvetica, Arial, sans-serif;
  background: #0e303a;
  color: #fff;
  text-align: center;
  padding: 40px 20px;
`;

const Logo = styled.img`
  width: 150px;
  margin-bottom: 48px;
  filter: brightness(0) invert(1);
`;

const Heading = styled.h1`
  font-size: 48px;
  font-weight: 700;
  margin: 0 0 8px;
`;

const SubHeading = styled.h2`
  font-size: 20px;
  font-weight: 400;
  margin: 0 0 32px;
  color: #a0c4cc;
`;

const RetryButton = styled.button`
  display: inline-block;
  padding: 12px 32px;
  background: #00a6c2;
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  margin-bottom: 16px;
  &:hover { background: #0094ab; }
`;

const SupportText = styled.p`
  font-size: 14px;
  color: #a0c4cc;
  a { color: #63dae2; text-decoration: none; &:hover { text-decoration: underline; } }
`;

const ErrorDetails = styled.details`
  margin-top: 24px;
  max-width: 600px;
  text-align: left;
  summary { cursor: pointer; color: #63dae2; font-size: 13px; }
  pre {
    background: rgba(0,0,0,0.3);
    padding: 16px;
    border-radius: 4px;
    overflow: auto;
    font-size: 12px;
    color: #a0c4cc;
    margin-top: 8px;
  }
`;

export default class AppErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Application error:', error, errorInfo);
    // Report to Honeybadger if available
    // Report to Honeybadger if configured (set in plugins/Honeybadger.tsx)
    const hb = (window as any).Honeybadger;
    if (hb && typeof hb.notify === 'function') {
      hb.notify(error, { context: { componentStack: errorInfo.componentStack } });
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render(): React.ReactElement {
    if (this.state.hasError) {
      return (
        <Container>
          <a href="/"><Logo src={logoSrc} alt="Contently" /></a>
          <Heading>Something went wrong.</Heading>
          <SubHeading>Sorry for the trouble!</SubHeading>
          <RetryButton onClick={this.handleRetry}>Try again</RetryButton>
          <SupportText>
            Keeps happening?{' '}
            <a href="mailto:support@contently.com">Contact Support</a>
          </SupportText>
          {this.state.error && (
            <ErrorDetails>
              <summary>Error details</summary>
              <pre>{this.state.error.message}{'\n'}{this.state.error.stack}</pre>
            </ErrorDetails>
          )}
        </Container>
      );
    }
    return this.props.children as React.ReactElement;
  }
}
